import * as THREE from 'three'
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader"
import {BufferGeometryUtils} from "three/examples/jsm/utils/BufferGeometryUtils";

class CarSceneController {
  /**
   *
   * @type {ThreeWrapper}
   * @private
   */
  _threeWrapper = null
  /**
   *
   * @type {CameraController}
   * @private
   */
  _cameraController = null

  /**
   *
   * @type {THREE.Object3D}
   * @private
   */
  _scene = null
  _animations = null

  /**
   *
   * @type {THREE.SpotLight}
   * @private
   */
  _mainLight = null
  _mainLightIntensity = 50

  _carMaterials = []
  _frame = 0

  _onModelLoad = null
  _onModelProgress = null

  _doorsOpened = false
  _trunkOpened = false
  _lightEnabled = true

  _ambientLightEnabled = false
  /**
   *
   * @type {THREE.PointLight}
   * @private
   */
  _ambientLightPoint = null
  /**
   *
   * @type {THREE.PointLight[]}
   * @private
   */
  _ambientDoorsLightPoints = []
  _ambientInterval = null
  _ambientColors = [
    0xffffff,
    0xED1B1B,
    0xED5A1B,
    0xED8A1B,
    0xFFFF33,
    0x33FFFC,
    0x1BEDEA,
    0x1B6AED,
    0x1B97ED,
    0xED1B9D]
  _ambientIndex = 0

  _carLights = []
  _insideLampsLights = []
  _jettaAmbientLights = []

  _interiorTextures = []

  /**
   *
   * @type {THREE.AnimationMixer}
   * @private
   */
  _animationMixer = null
  /**
   *
   * @type {Clock}
   * @private
   */
  _animationClock = new THREE.Clock()

  _renderAlwaysTimeout = null

  _isMobile = false
  _isChinaMobile = false

  _onDoorsOpened = null
  _onDoorsClosed = null
  _onTrunkOpened = null
  _onTrunkClosed = null

  constructor({
                isMobile,
                isChinaMobile,
                threeWrapper,
                cameraController,
                onModelLoad,
                onModelProgress,
                onDoorsOpened,
                onDoorsClosed,
                onTrunkOpened,
                onTrunkClosed,
              }) {
    this._isMobile = isMobile
    this._isChinaMobile = isChinaMobile
    this._threeWrapper = threeWrapper
    this._threeWrapper.addUpdateController(this)
    this._cameraController = cameraController

    this._onModelLoad = onModelLoad
    this._onModelProgress = onModelProgress

    this._onDoorsOpened = onDoorsOpened
    this._onDoorsClosed = onDoorsClosed
    this._onTrunkOpened = onTrunkOpened
    this._onTrunkClosed = onTrunkClosed

    this.loadModel()
    // this._onModelLoad()
  }

  stopRenderAlways(seconds) {
    this._threeWrapper.renderAlways = true

    if (this._renderAlwaysTimeout) {
      clearTimeout(this._renderAlwaysTimeout)
      this.renderAlwaysTimeout = null
    }
    this._renderAlwaysTimeout = setTimeout(() => {
      this._threeWrapper.renderAlways = false
    }, seconds * 1000)
  }

  toggleDoors () {
    if (this._animationMixer) {
      this._doorsOpened = !this._doorsOpened

      if (this._doorsOpened) {
        this._onDoorsOpened()
      }

      for (let i = 0; i < 4; i++) {
        this._animationMixer.clipAction(this._animations[i])

        const action = this._animationMixer.clipAction(this._animations[i])
        action.timeScale = this._doorsOpened ? 1 : -1
        action.setLoop(THREE.LoopOnce)
        action.paused = false
        action.clampWhenFinished = true
        action.play()
      }

      this.stopRenderAlways(6)
    }
  }

  toggleTrunk() {
    if (this._animationMixer) {
      this._trunkOpened = !this._trunkOpened
      this._animationMixer.clipAction(this._animations[4])

      if (this._trunkOpened) {
        this._onTrunkOpened()
      }

      const action = this._animationMixer.clipAction(this._animations[4])
      action.timeScale = this._trunkOpened ? 1 : -1
      action.setLoop(THREE.LoopOnce)
      action.paused = false
      action.clampWhenFinished = true
      action.play()

      this.stopRenderAlways(6)
    }
  }

  toggleAmbientLight () {

    if (this._ambientInterval) {
      clearInterval(this._ambientInterval)
      this._ambientInterval = null
    }

    this._ambientLightEnabled = !this._ambientLightEnabled

    this._threeWrapper._carAmbientLightsEnabled = this._ambientLightEnabled
    this.updateEnvMapIntensity()
    this.stopRenderAlways(1)

    if (this._ambientLightEnabled) {
      this._ambientInterval = setInterval(() => {
        this._ambientIndex++
        if (this._ambientIndex >= this._ambientColors.length) {
          this._ambientIndex = 0
        }

        this.updateEnvMapIntensity()
        this.stopRenderAlways(1)
      }, 1100)
    }
  }

  toggleLights() {
    this._lightEnabled = !this._lightEnabled

    this._mainLight.intensity = this._lightEnabled ? this._mainLightIntensity : 8

    this._carLights.forEach((light, index) => {
      light.intensity = index < 2 ? this.getCarLightIntensity() : !this._lightEnabled ? 1 : 0
    })

    this._carLights[2].intensity = this._lightEnabled ? 0 : 5
    this._carLights[3].intensity = this._lightEnabled ? 0 : 5

    this._insideLampsLights[0].intensity = this._lightEnabled ? 0 : 5
    this._insideLampsLights[1].intensity = this._lightEnabled ? 0 : 5
    this._insideLampsLights[2].intensity = this._lightEnabled ? 0 : 1
    this._insideLampsLights[3].intensity = this._lightEnabled ? 0 : 1

    // this._insideLampsLights.forEach((light) => {
    //   light.visible = !this._lightEnabled
    // })

    this._threeWrapper._carLightsEnabled = !this._lightEnabled
    this.updateEnvMapIntensity()

    this.stopRenderAlways(1)

    return this._lightEnabled
  }

  updateEnvMapIntensity() {
    const envMapIntensity = this._lightEnabled && !this._ambientLightEnabled ? 2.5 : 1
    // const envMapIntensity = this._lightEnabled ? 2.5 : 1

    this._threeWrapper.scene.traverse(item => {
      if (item.name === 'WALL_CONCRETE_FOTO') {
        item.envMapIntensity = this._lightEnabled ? 2 : 1
      }

      if (item.name === 'WALL_EM_1') {
        item.material.envMap = this._threeWrapper.envMap
        item.material.envMapIntensity = this._lightEnabled ? 1 : 0.5
      }

      if (item.name === 'WALL_EM_2') {
        item.material.envMap = this._threeWrapper.envMap
        item.material.envMapIntensity = this._lightEnabled ? 1 : 0.5
      }

      if (item.name === '0_DV_1_L') {
        item.material.emissive = new THREE.Color(0xffffff)
      }
      if (item.name === 'FOOR_CONCRETE_CAR') {
        item.receiveShadow = true
      }
      if (item.name === 'DNO') {
        // item.castShadow = true
      }
      if (item.name === 'JETTA_MID' || item.name === 'ANIM_DOOR') {
        item.traverse(child => {
          if (child.material) {
            child.material.envMap = this._threeWrapper.envMap
            child.material.envMapIntensity = envMapIntensity
          }
        })
      }

      if (item.name === 'FARA_FRONT_EM') {
        if (this._lightEnabled) {
          item.material.emissive = new THREE.Color(0x000000)
        } else {
          item.material.emissive = new THREE.Color(0xffffff)
        }
      } else if (item.name === 'FARA_BACK_EM' || item.name === 'FARA_BAGAZH_EM') {
        if (this._lightEnabled) {
          item.material.emissive = new THREE.Color(0x000000)
        } else {
          item.material.emissive = new THREE.Color(0xff1a1a)
        }
      }

      if (item.name === 'L1_BACKLIGHT') {
        item.material.emissiveIntensity = 0.2
        if (!this._ambientLightEnabled) {
          this._ambientLightPoint.intensity = 0
          this._ambientDoorsLightPoints.forEach((light) => {
            light.intensity = 0
          })
          item.material.emissive = new THREE.Color(0x000000)
          item.material.color = new THREE.Color(0x000000)
          item.layers.disable(1)
        } else {
          this._ambientLightPoint.intensity = 2.46
          this._ambientDoorsLightPoints.forEach((light) => {
            light.intensity = 1
            light.color = new THREE.Color(this._ambientColors[this._ambientIndex])
          })
          this._ambientLightPoint.color = new THREE.Color(this._ambientColors[this._ambientIndex])
          item.material.emissive = new THREE.Color(this._ambientColors[this._ambientIndex])
          item.material.color = new THREE.Color(this._ambientColors[this._ambientIndex])
          item.layers.enable(1)
        }
      }
    })
  }

  getCarLightIntensity() {
    return this._lightEnabled ? 0 : 100
  }

  update() {
    this._frame++
    const delta = this._animationClock.getDelta()
    if (this._animationMixer) {
      this._animationMixer.update(delta)
    }
    // this.setCarColor(this._frame * 256)
  }

  async processProgress(response)  {
    //const total = Number(response.headers.get('content-length'))
    const total = 37 * 1024 *1024
    const reader = response.body.getReader()
    const chunks = []
    let bytesReceived = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const result = await reader.read()
      if (result.done) {
        break;
      }

      chunks.push(result.value)
      bytesReceived += result.value.length

      if (this._onModelProgress) this._onModelProgress(bytesReceived/total)
    }

    let chunksAll = new Uint8Array(total)
    let position = 0
    for(let chunk of chunks) {
      chunksAll.set(chunk, position)
      position += chunk.length;
    }
  }

  loadModel() {

    const lightmap = new THREE.TextureLoader().load('/3d/models/jetta/lightmap.png');
    const loader = new GLTFLoader()
    loader.setPath('/3d/models/jetta/')
    loader.load('JETTA_APP_M.glb', (gltf) => {
      this._scene = gltf.scene
      this._animations = gltf.animations

      let gpuHasFrontFacingDoubleSidedBug = false
      let debugInfo = this._threeWrapper.renderer.getContext().getExtension('WEBGL_debug_renderer_info')
      if (debugInfo !== null) {
        let gpu = this._threeWrapper.renderer.getContext().getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        gpuHasFrontFacingDoubleSidedBug = gpu.match(/adreno.+(5|6)[0-9][0-9]/gi) !== null
      }

      this._scene.traverse((node) => {
        if (node.name === 'L1') {
          node.material.emissiveMap = lightmap
        }
        if (node.parent &&
          node.material &&
          node.material.normalMap &&
          node.material.side === THREE.DoubleSide &&
          gpuHasFrontFacingDoubleSidedBug) {
          BufferGeometryUtils.computeTangents(node.geometry)
          node.material.vertexTangents = true
          node.material.needsUpdate = true
        }
      })

      // this._isChinaMobile = true
      // if (this._isChinaMobile) {
      //   this._scene.traverse(item => {
      //     if (item.name === 'JETTA_SALON_FINAL') {
      //       item.traverse(salonItem => {
      //         if (salonItem.material) {
      //           // salonItem.material.metalness = 0
      //           salonItem.material.roughnessMap = null
      //           salonItem.material.roughness = 1
      //           // salonItem.material.metalnessMap = null
      //           // salonItem.material.normalMap = null
      //           // console.log(salonItem.name)
      //         }
      //       })
      //     }
      //   })
      // }

      this._threeWrapper.scene.add(this._scene)

      this._animationMixer = new THREE.AnimationMixer(this._scene)
      this._animationMixer.addEventListener('finished', (e) => {
        if (e.action._clip === this._animations[0] && !this._doorsOpened) {
          this._onDoorsClosed()
        } else if (e.action._clip === this._animations[4] && !this._trunkOpened) {
          this._onTrunkClosed()
        }
      })

      if (this._onModelLoad) this._onModelLoad()

      this.createLights()
      this.updateEnvMapIntensity()
      // this.setCarColor({id: 3})
    }, (e) => {
      this._onModelProgress(e.loaded / e.total)
    })
  }

  setInteriorColor(colorData) {
    const data = {
      '1': {
        'L3': {
          color: new THREE.Color(0x574432),
        }
      },
      '2': {
        'L3': {
          color: new THREE.Color(0x000000),
        }
      }
    }

    let materialsSetup = data[colorData.id] ? data[colorData.id] : null

    if (materialsSetup) {
      let names = []
      for (const name in materialsSetup) names.push(name)

      this._threeWrapper.scene.traverse(item => {
        if (names.indexOf(item.name) >= 0 && item.material) {
          if (this._interiorTextures.length === 0) {
            // this._interiorTextures[0] = item.material.map
            // this._interiorTextures[0] = item.material.alphaMap
            // console.log(item.material)
          }

          // if (colorData.id === 2) {
          //   item.material.map = this._interiorTextures[0]
          // } else {
          //   item.material.map = null
          // }

          const options = materialsSetup[item.name]
          for (const option in options) {
            item.material[option] = options[option]
          }
          item.material.needsUpdate = true
        }
      })

      this._threeWrapper.render()
    }
  }

  setCarColor(colorData) {
    // if (this._carMaterial) this._carMaterial.color = new THREE.Color(color)
    const data = {
      '1': {
        'KRISHA': {
          color: new THREE.Color( 0x5b0501),
          roughness: 0.3,
          metalness: 0.5,
        },
      },
      '2': {
        'KRISHA': {
          color: new THREE.Color(0xe8e8e8),
          roughness: 0.25,
          metalness: 0.15,
        },
      },
      '3': {
        'KRISHA': {
          color: new THREE.Color(0x000a19),
          roughness: 0.2,
          metalness: 0.5,
        },
      },
      '4': {
        'KRISHA': {
          color: new THREE.Color(0x0f0f0f),
          roughness: 0.3,
          metalness: 0.5
        },
      },
      '5': {
        'KRISHA': {
          color: new THREE.Color(0x000000),
          roughness: 0.14,
          metalness: 0.40,
        },
      },
      '6': {
        'KRISHA': {
          color: new THREE.Color(0x2D2D2D),
          roughness: 0.3,
          metalness: 0.5
        },
      }
    }

    let materialsSetup = data[colorData.id] ? data[colorData.id] : null

    if (materialsSetup) {
      let names = []
      for (const name in materialsSetup) names.push(name)

      this._threeWrapper.scene.traverse(item => {
        // console.log(item.name)
        if (names.indexOf(item.name) >= 0 && item.material) {
          const options = materialsSetup[item.name]
          for (const option in options) {
            item.material[option] = options[option]
          }
          item.material.needsUpdate = true
        }
      })

      this._threeWrapper.render()
    }
  }

  createLights() {

    this._mainLight = new THREE.SpotLight(0xffffff, this._mainLightIntensity)
    this._mainLight.penumbra = 0.34
    this._mainLight.angle = 1.054
    this._mainLight.position.set(0.075, 10.629, 0.487)
    this._mainLight.lookAt(0,0,0)
    this._mainLight.castShadow = false
    this._threeWrapper.scene.add(this._mainLight)

    const directLight = new THREE.SpotLight(0xf2f2f2, 20)
    directLight.angle = Math.PI / 2
    directLight.penumbra = 1
    directLight.position.set(0, 1.5, 0)
    directLight.castShadow = false
    directLight.target = new THREE.Mesh()
    directLight.target.position.set(0, 5, 0)
    this._scene.add(directLight.target)
    this._scene.add(directLight)

    // this._mainLight.castShadow = true
    // this._mainLight.shadow.radius = 3
    // this._mainLight.shadow.mapSize = new THREE.Vector2(this._isMobile ? 256 : 2048, this._isMobile ? 256 : 2048)
    // this._mainLight.angle = Math.PI / 2

    if (this._carLights.length === 0) {
      this._insideLampsLights.push(new THREE.PointLight(0xfffffff, 0, 0.58, 2.50))
      this._insideLampsLights.push(new THREE.PointLight(0xfffffff, 0, 0.58, 2.50))
      this._insideLampsLights.push(new THREE.PointLight(0xff2222, 0, 0.5, 1.20))
      this._insideLampsLights.push(new THREE.PointLight(0xff2222, 0, 0.5, 1.20))

      this._insideLampsLights[0].position.set(0.671, 0.863, 2.025)
      this._insideLampsLights[1].position.set(-0.678, 0.875, 2.025)
      this._insideLampsLights[2].position.set(0.700, 0.997, -2.220)
      this._insideLampsLights[3].position.set(-0.720, 1.066, -2.220)

      this._insideLampsLights.forEach(light => {
        light.frustumCulled = true
        this._threeWrapper.scene.add(light)
      })


      this._carLights.push(new THREE.SpotLight(0xffffff, 0, 100))
      this._carLights.push(new THREE.SpotLight(0xffffff, 0, 100))
      this._carLights.push(new THREE.SpotLight(0xeeeeff, 0, 100))
      this._carLights.push(new THREE.SpotLight(0xeeeeff, 0, 100))
      // this._carLights[0].position.set(0, 0.5, 2.6)
      // this._carLights.push(new THREE.SpotLight(0xffff00, 1))

      this._carLights[0].position.set(0.7, 0.5, 2.1)
      this._carLights[1].position.set(-0.7, 0.5, 2.1)
      this._carLights[2].position.set(0.7, 0.4, 2.4)
      this._carLights[3].position.set(-0.7, 0.4, 2.4)
      // this._carLights[0].lookAt(new THREE.Vector3(this._carLights[0].position.x, this._carLights[0].position.y, this._carLights[0].position.z + 1))
      // this._carLights[1].position.set(0.3, 0.3, 2.2)

      this._ambientLightPoint = new THREE.PointLight(0xffffff, 2.46,0.20)
      this._ambientLightPoint.position.set(0,0.824,0.585)
      this._ambientLightPoint.intensity = 0
      this._threeWrapper.scene.add(this._ambientLightPoint)

      this._ambientDoorsLightPoints.push(new THREE.PointLight(0xff0000, 1.68,0.22))
      this._ambientDoorsLightPoints.push(new THREE.PointLight(0xff0000, 1.68,0.22))

      this._ambientDoorsLightPoints[0].position.set(0.626,0.950,0.356)
      this._ambientDoorsLightPoints[1].position.set(-0.626,0.950,0.356)

      this._ambientDoorsLightPoints.forEach((light) => {
        light.intensity = 0
        this._threeWrapper.scene.add(light)
      })

      this._carLights.forEach(light => {
        this._threeWrapper.scene.add(light)
        this._threeWrapper.scene.add(light.target)
        light.lookAt(0, 0, 1000)
        light.target.position.set(light.position.x, light.position.y, light.position.z + 1)
        light.penumbra = 1
        light.angle = Math.PI / 4.5
        light.intensity = 0
        light.decay = 1
        light.castShadow = false
      })

      this._carLights[2].angle = Math.PI / 2
      this._carLights[3].angle = Math.PI / 2
    }

    const cubeTexture = new THREE.CubeTextureLoader()
      .setPath( '/' )
      .load( [
        '1x1_white.png',
        '1x1_white.png',
        '1x1_white.png',
        '1x1_white.png',
        '1x1_white.png',
        '1x1_white.png'
      ] )

    this._scene.traverse((sceneItem) => {
      if (sceneItem.isMesh) {

        if (sceneItem.name === 'WALL_CONCRETE_FOTO') {
          sceneItem.material = sceneItem.material.clone()
          sceneItem.material.envMap = cubeTexture
          sceneItem.material.envMapIntensity = 2
          sceneItem.material.color = new THREE.Color(0xffffff)
          sceneItem.material.emissive = new THREE.Color(0x000000)
        }

        if (sceneItem.name === 'WALL_EM_1') {
          sceneItem.material.envMap = this._threeWrapper.envMap
          sceneItem.material.envMapIntensity = 2
        }

        if (sceneItem.name === 'WALL_EM_2') {
          sceneItem.material.envMap = this._threeWrapper.envMap
          sceneItem.material.envMapIntensity = 2
        }

        if (sceneItem.name.indexOf('_EM') >= 0) {
          if (sceneItem.name === 'TOP_EM') {
            // sceneItem.material.roughness = 1
            // sceneItem.material.metalness = 0.01
            // sceneItem.material.color = new THREE.Color(0xffffff)
            // sceneItem.material.emissive = new THREE.Color(0x000000)
            // sceneItem.material.envMap = cubeTexture
            // sceneItem.material.envMapIntensity = 100
          } else if (sceneItem.name === 'BOTTOM_LINE_EM' ||
            sceneItem.name === 'JETTA_EM' ||
            sceneItem.name === 'YOUNIVERSE_EM' ||
            sceneItem.name === 'WALL_LOGO_LINE_EM' ||
            sceneItem.name === 'VW_LOGO_EM' ||
            sceneItem.name === 'TOP_LINE_EM') {
            // sceneItem.material = new THREE.MeshPhysicalMaterial({
            //   color: new THREE.Color(0xffffff),
            //   roughness: 0,
            //   metalness: 1
            // })
            sceneItem.material.color = new THREE.Color(0xffffff)
            sceneItem.material.emissive = new THREE.Color(0x000000)
            sceneItem.material.roughness = 1
            sceneItem.material.metalness = 1
            sceneItem.material.envMap = cubeTexture
            sceneItem.material.envMapIntensity = 200
            // sceneItem.material.ambientLightColor = new THREE.Color(0xffffff)
            // sceneItem.layers.enable(1)
          } else {
            const carGlowingLights = ['FARA_FRONT_EM', 'TUMAN_EM', 'FARA_BACK_EM', 'FARA_BAGAZH_EM']
            if (carGlowingLights.indexOf(sceneItem.name) >= 0) {
              sceneItem.layers.enable(1)
            }
          }
        } else if (sceneItem.name === 'L1_BACKLIGHT') {
          sceneItem.visible = false
        } else {
          sceneItem.layers.enable(0)
        }
      }
    })
  }
}

export default CarSceneController